/** Shared enums and constants used by both the API and the UI. */

export const SECTION_SLUGS = {
  HQ: 'hq',
  REAL_ESTATE: 'realestate',
  AGENCY: 'agency',
  BROKERS: 'brokers',
  CONSTRUCTION: 'construction',
  /** Cleaning jobs — houses, apartments, shops, offices (docs/10 §50). */
  CLEANER: 'cleaner',
};

export const SECTIONS = [
  {
    slug: SECTION_SLUGS.REAL_ESTATE,
    code: 'RE',
    name: 'Real Estate',
    nameAr: 'العقارات',
    nameCkb: 'عقارات',
    color: '199 89% 42%',
  },
  {
    slug: SECTION_SLUGS.AGENCY,
    code: 'AG',
    name: 'Agency',
    nameAr: 'الوكالة',
    nameCkb: 'ئەیگێنسی',
    color: '262 70% 55%',
  },
  {
    slug: SECTION_SLUGS.BROKERS,
    code: 'BR',
    name: 'Brokers',
    nameAr: 'الوسطاء',
    nameCkb: 'دەلاڵەکان',
    color: '35 92% 48%',
  },
  {
    slug: SECTION_SLUGS.CONSTRUCTION,
    code: 'CN',
    name: 'Construction',
    nameAr: 'المقاولات',
    nameCkb: 'مقاولات',
    color: '158 64% 38%',
  },
  {
    /*
     * Before HQ, because `sortOrder` is the array index and head office is last on every
     * list. Re-seeding moves HQ from 4 to 5; nothing reads the number but the sort.
     */
    slug: SECTION_SLUGS.CLEANER,
    code: 'CL',
    name: 'Cleaner',
    nameAr: 'التنظيف',
    nameCkb: 'پاککردنەوە',
    color: '322 65% 45%',
  },
  {
    slug: SECTION_SLUGS.HQ,
    code: 'HQ',
    name: 'Head Office',
    nameAr: 'الإدارة العامة',
    nameCkb: 'بارەگای سەرەکی',
    color: '222 47% 24%',
  },
];

// --- accounting -------------------------------------------------------------

export const ACCOUNT_TYPE = {
  ASSET: 'asset',
  LIABILITY: 'liability',
  EQUITY: 'equity',
  REVENUE: 'revenue',
  EXPENSE: 'expense',
};

export const NORMAL_BALANCE = {
  [ACCOUNT_TYPE.ASSET]: 'debit',
  [ACCOUNT_TYPE.EXPENSE]: 'debit',
  [ACCOUNT_TYPE.LIABILITY]: 'credit',
  [ACCOUNT_TYPE.EQUITY]: 'credit',
  [ACCOUNT_TYPE.REVENUE]: 'credit',
};

export const ACCOUNT_SUBTYPE = [
  'cash',
  'bank',
  'receivable',
  'inventory',
  /*
   * What a Treat holds while it is held (docs/10 §3.2, account `1320`).
   *
   * Not `inventory`: inventory is stock the group bought to trade on its own account, and
   * this is one broker's money parked in a thing. They sit on the same side of the balance
   * sheet and answer different questions, and the Safe report has to tell them apart to say
   * how much of the box is the group's own.
   */
  'trading_asset',
  'fixed_asset',
  'prepaid',
  'wip',
  'other_asset',
  'payable',
  'accrued',
  'tax_payable',
  'salary_payable',
  'payroll_deductions_payable',
  'commission_payable',
  'partner_payable',
  /* What the group owes its brokers (docs/10 §3.1, account `2180`). */
  'broker_capital',
  /* …and the part of that debt currently sitting in an open treat (`2185`). */
  'broker_treat_investment',
  /* What a property owner paid in and the group has not yet spent on his site (`2190`). */
  'owner_advance',
  'retention_payable',
  'loan',
  'other_liability',
  'capital',
  'retained_earnings',
  'drawings',
  'sales_revenue',
  'rental_revenue',
  'commission_revenue',
  'service_revenue',
  'other_income',
  'cogs',
  'salary_expense',
  'commission_expense',
  'opex',
  'depreciation',
  'other_expense',
];

export const PERIOD_STATUS = { OPEN: 'open', LOCKED: 'locked', CLOSED: 'closed' };

export const JOURNAL_STATUS = { DRAFT: 'draft', POSTED: 'posted', REVERSED: 'reversed' };

export const JOURNAL_SOURCE = {
  INVOICE: 'invoice',
  PAYMENT: 'payment',
  EXPENSE: 'expense',
  PAYROLL: 'payroll',
  COMMISSION: 'commission',
  TRANSFER: 'transfer',
  PROFIT: 'profit',
  OPENING: 'opening',
  MANUAL: 'manual',
  REVERSAL: 'reversal',
  CLOSING: 'closing',
  ADJUSTMENT: 'adjustment',
};

// --- treasury ---------------------------------------------------------------

export const SAFE_TYPE = { MAIN: 'main', SECTION: 'section', PETTY: 'petty', BANK: 'bank' };

export const SAFE_DIRECTION = { IN: 'in', OUT: 'out' };

// --- documents --------------------------------------------------------------

export const DOC_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  POSTED: 'posted',
  PARTIALLY_PAID: 'partially_paid',
  PAID: 'paid',
  OVERDUE: 'overdue',
  REJECTED: 'rejected',
  VOID: 'void',
  REVERSED: 'reversed',
};

/** Statuses that mean "this document has hit the ledger and is now immutable" (I5). */
export const IMMUTABLE_STATUSES = [
  DOC_STATUS.POSTED,
  DOC_STATUS.PARTIALLY_PAID,
  DOC_STATUS.PAID,
  DOC_STATUS.OVERDUE,
  DOC_STATUS.REVERSED,
];

export const INVOICE_KIND = { INCOME: 'income', OUTCOME: 'outcome' };

/**
 * **What the invoice is for**, on top of which direction it points (docs/10 §1).
 *
 * `kind` answers "does this bring money in or send it out?"; `type` answers "on account of
 * what?" — and Real Estate is the section that cannot report without the second. A month's
 * income invoices there are rent, deposits taken, commissions earned and plain collections
 * on a sale, and a register that shows one `income` total for all four tells the manager
 * nothing they did not already know.
 *
 * The keys are English slugs so the ledger, the export and the URL are stable; the names
 * the section actually uses are the locale strings on `documents:invoices.types.*`
 * (پارەوەرگرتن · پارەدان · تەئمینات · وەرگرتنی کرێ · عموڵە).
 *
 * Deliberately **not** the same list as `PAYMENT_KIND`. A receipt here is an invoice
 * raising a receivable for money to be collected; a `payment` document is the cash that
 * settles one. They share a word in Kurdish and nothing else.
 */
export const INVOICE_TYPE = {
  /** پارەوەرگرتن — a collection on account: sale instalments, transfers, settlements. */
  RECEIPT: 'receipt',
  /** پارەدان — money the section owes out on account. */
  PAYMENT: 'payment',
  /** تەئمینات — a security deposit, held rather than earned. Either direction. */
  DEPOSIT: 'deposit',
  /** وەرگرتنی کرێ — rent billed to a tenant. */
  RENT: 'rent',
  /** عموڵە — brokerage/agency commission, earned or payable. */
  COMMISSION: 'commission',
};

/**
 * Which types each direction may carry.
 *
 * Three of the five are inherently one-way — money *received* is not an outcome invoice
 * and money *paid* is not an income one — while a deposit and a commission are genuinely
 * both: the section takes a deposit from a tenant and pays one to a landlord, and it earns
 * commission and pays it away. Enforcing this is what stops the register's type breakdown
 * from filling up with rows nobody can explain.
 */
export const INVOICE_TYPES_BY_KIND = {
  [INVOICE_KIND.INCOME]: [
    INVOICE_TYPE.RECEIPT,
    INVOICE_TYPE.RENT,
    INVOICE_TYPE.DEPOSIT,
    INVOICE_TYPE.COMMISSION,
  ],
  [INVOICE_KIND.OUTCOME]: [INVOICE_TYPE.PAYMENT, INVOICE_TYPE.DEPOSIT, INVOICE_TYPE.COMMISSION],
};

/**
 * **Which sections use types, and which ones.**
 *
 * Sections do not share an invoice register — every invoice carries its own `sectionId`
 * and every query is scoped to it (**I7**), so Real Estate's invoices, its list and its
 * report are its own. The type set is part of that ownership: these five are how *Real
 * Estate* reads its month, not a group-wide taxonomy, and a section absent from this map
 * is never asked the question.
 *
 * A map rather than a flag because the answer is two facts — whether a section types its
 * invoices at all, and which types it uses — and a second section adopting them (with its
 * own list, most likely) is a line here rather than a change anywhere else.
 *
 * **Agency takes two of the five**, and that is the whole point of the map being a map.
 * Its register is money in and money out against a customer — پارەوەرگرتن and پارەدان —
 * so it declares exactly those and never sees rent or a security deposit in the picker.
 * Intersected with the direction (`INVOICE_TYPES_BY_KIND`) this leaves one valid answer
 * per `kind`, which is intentional: the field is what the *statement* reads the line as,
 * and having it stated on the document beats inferring it from the direction later.
 */
export const INVOICE_TYPES_BY_SECTION = {
  [SECTION_SLUGS.REAL_ESTATE]: Object.values(INVOICE_TYPE),
  [SECTION_SLUGS.AGENCY]: [INVOICE_TYPE.RECEIPT, INVOICE_TYPE.PAYMENT],
};

/** The types this section works in, in display order. Empty for a section that uses none. */
export const invoiceTypesFor = (sectionSlug) => INVOICE_TYPES_BY_SECTION[sectionSlug] ?? [];

/**
 * Does this section type its invoices?
 *
 * Where it does, a type is **required** — an untyped Real Estate invoice drops out of
 * every breakdown on that section's own report, which is the one report it opens. Where
 * it does not, the field is simply never asked for; it is not refused, because the
 * verticals raise invoices through the same service and a lease instalment knows perfectly
 * well that it is rent whichever section is billing it.
 */
export const sectionUsesInvoiceTypes = (sectionSlug) => invoiceTypesFor(sectionSlug).length > 0;

/** Does this type belong on this direction? Used by the schema, the service and the form. */
export const isInvoiceTypeAllowed = (kind, type) => !type || !!INVOICE_TYPES_BY_KIND[kind]?.includes(type);

/**
 * **The direction a type implies, when it implies exactly one.**
 *
 * Money *received* is an income invoice and money *paid* is an outcome one — those two
 * carry their direction in their name, and in Kurdish they are the name: پارەوەرگرتن and
 * پارەدان. Rent is likewise only ever billed in. A deposit and a commission are genuinely
 * both, so they imply nothing and this returns `null` for them.
 *
 * The form uses it to stop asking the same question twice. Offering only the types that
 * fit the direction already chosen means a section whose whole vocabulary is "money in,
 * money out" — Agency — shows a one-item picker, and the operator reads a list with
 * پارەوەرگرتن in it and no پارەدان as the second one being missing rather than as being
 * one control away. Letting the *reason* set the direction inverts that: pick what the
 * document is for, and which way it points follows.
 *
 * `null` is the honest answer, not a default: guessing `income` for a deposit would
 * silently flip a landlord's deposit into a receivable.
 */
export const kindForInvoiceType = (type) => {
  const kinds = Object.keys(INVOICE_TYPES_BY_KIND).filter((kind) =>
    INVOICE_TYPES_BY_KIND[kind].includes(type),
  );
  return kinds.length === 1 ? kinds[0] : null;
};

export const PAYMENT_KIND = { RECEIPT: 'receipt', DISBURSEMENT: 'disbursement' };

export const PAYMENT_METHOD = {
  CASH: 'cash',
  BANK: 'bank',
  CHEQUE: 'cheque',
  TRANSFER: 'transfer',
  CARD: 'card',
};

export const EXPENSE_PAYMENT_MODE = {
  CASH_FROM_SAFE: 'cash_from_safe',
  PAYABLE: 'payable',
  REIMBURSABLE: 'reimbursable',
};

export const PARTY_ROLE = {
  CUSTOMER: 'customer',
  VENDOR: 'vendor',
  EMPLOYEE: 'employee',
  BROKER: 'broker',
  PARTNER: 'partner',
  OWNER: 'owner',
  TENANT: 'tenant',
  SUBCONTRACTOR: 'subcontractor',
};

/**
 * **Sections whose invoices face one register in both directions** (docs/10 §4.3).
 *
 * Normally the counterparty follows the *direction*: an income invoice is to a customer or
 * a tenant, an outcome one to a vendor, an owner or a subcontractor. Two registers, and
 * `kind` picks between them.
 *
 * Agency does not work that way. It bills a customer (پارەوەرگرتن) and it pays that same
 * customer (پارەدان), and the point of its customer report is a single running balance that
 * goes up and down — which only exists if both documents point at one party. Sending the
 * پارەدان to a vendor instead would put the two halves of one relationship in two registers
 * that never meet, and the balance the section actually wants could not be computed at all.
 *
 * A section listed here **adds** that role to both directions; it does not replace them.
 * Agency buys printing and media like anyone else, and a vendor bill is still a vendor
 * bill — forbidding it to make the customer case work would fix one document by deleting
 * another. What the declaration changes is that `customer` becomes *available* on the way
 * out, and **first**, which is what a typed name is created as.
 *
 * A section absent from the map is unchanged and keeps the plain two-register rule.
 */
export const INVOICE_PARTY_ROLE_BY_SECTION = {
  [SECTION_SLUGS.AGENCY]: PARTY_ROLE.CUSTOMER,
};

/** The register this section's invoices face in *both* directions, or `null` for neither. */
export const invoicePartyRoleFor = (sectionSlug) => INVOICE_PARTY_ROLE_BY_SECTION[sectionSlug] ?? null;

/**
 * **Who may be on the other side of each kind of invoice** — the one list, shared.
 *
 * The server has always held this (`invoice.service.js`, `ROLE_FOR_KIND`) and widened it
 * as the verticals needed: rent is billed to a `tenant`, an `owner` is paid their share, a
 * `subcontractor` their certificate, and a member of staff a commission or an expense
 * claim (docs/10 §14.8). The invoice form kept its own two-word copy — customer or vendor
 * — so on an edit it looked the document's party up in the wrong register, found nobody,
 * and refused to save an invoice the server had accepted. Two lists of one fact drift;
 * this is the fact.
 *
 * **Order is load-bearing.** `roles[0]` is what a *typed* name is registered as, so
 * `employee` sits last on the payable side or every new supplier would become staff.
 */
export const INVOICE_PARTY_ROLES_BY_KIND = {
  [INVOICE_KIND.INCOME]: [PARTY_ROLE.CUSTOMER, PARTY_ROLE.TENANT],
  [INVOICE_KIND.OUTCOME]: [
    PARTY_ROLE.VENDOR,
    PARTY_ROLE.OWNER,
    PARTY_ROLE.SUBCONTRACTOR,
    PARTY_ROLE.EMPLOYEE,
  ],
};

/**
 * The roles an invoice of this kind may face in this section, the section's own register
 * first where it keeps one (`invoicePartyRoleFor`) — prepended, never substituted, for the
 * reasons on that map.
 */
export const invoicePartyRolesFor = (sectionSlug, kind) => {
  const base = INVOICE_PARTY_ROLES_BY_KIND[kind] ?? [];
  const extra = invoicePartyRoleFor(sectionSlug);
  return extra && !base.includes(extra) ? [extra, ...base] : base;
};

// --- commissions ------------------------------------------------------------

export const COMMISSION_BASE = {
  GROSS_AMOUNT: 'gross_amount',
  NET_PROFIT: 'net_profit',
  COLLECTED_AMOUNT: 'collected_amount',
  MARGIN: 'margin',
  CUSTOM: 'custom',
};

export const COMMISSION_METHOD = {
  PERCENT: 'percent',
  FIXED: 'fixed',
  TIERED: 'tiered',
  PROGRESSIVE: 'progressive',
};

export const COMMISSION_TRIGGER = {
  ON_POST: 'on_post',
  ON_COLLECTION: 'on_collection',
  ON_COMPLETION: 'on_completion',
};

export const COMMISSION_PAYOUT_TRIGGER = {
  IMMEDIATE: 'immediate',
  WITH_PAYROLL: 'with_payroll',
  MANUAL: 'manual',
};

export const COMMISSION_STATUS = {
  ACCRUED: 'accrued',
  APPROVED: 'approved',
  PAYABLE: 'payable',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

export const BENEFICIARY_ROLE = {
  BROKER: 'broker',
  /**
   * The salesperson who earned it — resolved from `Invoice.earnedByPartyIds` (docs/10 §5.2).
   *
   * A plan's `employee` split pays **one** of them: the id the caller passes, or the first
   * name on the document. Per-earner money is the employee-terms path (§5.1a), which accrues
   * one commission per person rather than splitting a group-wide rate between them.
   */
  EMPLOYEE: 'employee',
  AGENT: 'agent',
  REFERRER: 'referrer',
  COMPANY: 'company',
  PARTNER: 'partner',
  MANAGER: 'manager',
};

export const CONDITION_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'between'];

// --- profit -----------------------------------------------------------------

// --- payroll ----------------------------------------------------------------

/**
 * How an employee is paid (docs/10 §5.1).
 *
 * Some of the group's people are on a salary, some earn only commission, and some both.
 * Before this the difference was *implied* by whether `baseSalary` happened to be set,
 * which made a salaried employee somebody forgot to fill in indistinguishable from a
 * commission-only earner — and payroll silently skipped both.
 */
export const COMPENSATION_MODE = {
  SALARY: 'salary',
  COMMISSION: 'commission',
  BOTH: 'both',
};

/**
 * **How much commission this employee earns** (docs/10 §5.1a) — the figure
 * `COMPENSATION_MODE` implied and never let anybody write down.
 *
 * `compensationMode: commission | both` could be set from the day it shipped, with nowhere
 * to say what the commission actually was: the only source of a rate was a `CommissionPlan`,
 * which prices a *document* by its own rules and names the earner through a split. That
 * works for a group-wide arrangement and not for "Aram gets 2.5%, Dana gets 500,000 a deal",
 * which is how this group pays its people.
 *
 * Three values rather than two, because `none` is a real answer and not a missing one: a
 * salaried employee earns no commission, and inferring that from a blank rate is the exact
 * ambiguity `COMPENSATION_MODE` exists to have removed.
 *
 * The two paying modes are mutually exclusive on purpose. An employee owed *both* a flat
 * amount and a percentage of the same invoice is two arrangements, and storing them in one
 * record would leave the second one unnamed on every payslip it appeared in.
 */
export const EMPLOYEE_COMMISSION_MODE = {
  /** Earns no commission. The default, and what every salaried employee keeps. */
  NONE: 'none',
  /** A flat sum per invoice they are named on, in their own currency. */
  AMOUNT: 'amount',
  /** A share of the invoice's commissionable amount, in basis points. */
  PERCENT: 'percent',
};

export const PAYROLL_STATUS = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  POSTED: 'posted',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

/** Per payslip line, so a run can be paid employee by employee. */
export const PAYSLIP_PAYMENT_STATUS = {
  UNPAID: 'unpaid',
  PAID: 'paid',
};

/**
 * A statement is a **snapshot**, not a live query (docs/02 §7): `final` freezes the
 * figures a distribution was computed from, so a later backdated entry cannot silently
 * change what partners were told they were owed.
 */
export const PROFIT_STATEMENT_STATUS = {
  DRAFT: 'draft',
  FINAL: 'final',
};

export const DISTRIBUTION_STATUS = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  POSTED: 'posted',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

export const PROFIT_SHARE_ROLE = {
  COMPANY: 'company',
  BROKER: 'broker',
  PARTNER: 'partner',
  INVESTOR: 'investor',
};

// --- verticals --------------------------------------------------------------

export const PROPERTY_STATUS = {
  AVAILABLE: 'available',
  RESERVED: 'reserved',
  SOLD: 'sold',
  RENTED: 'rented',
  MAINTENANCE: 'maintenance',
};

export const PROPERTY_TYPE = ['apartment', 'villa', 'land', 'office', 'shop', 'warehouse', 'building'];

/**
 * Contract lifecycle, shared by sale contracts, leases and service contracts.
 *
 * Distinct from `DOC_STATUS` on purpose: a contract is not posted, it is *signed*, and it
 * then runs for a period. `active` is the state that generates invoices, which is why
 * `terminate` and `expire` are different endings — one is a decision, the other is a date.
 */
export const CONTRACT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  TERMINATED: 'terminated',
  CANCELLED: 'cancelled',
};

/** One row of a sale contract's payment plan, or one rent period of a lease. */
export const INSTALLMENT_STATUS = {
  PENDING: 'pending',
  INVOICED: 'invoiced',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled',
};

export const LEASE_FREQUENCY = {
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  SEMIANNUAL: 'semiannual',
  ANNUAL: 'annual',
};

/**
 * **A Real Estate tenancy** — who rents what, for how long, and on what rhythm.
 *
 * Deliberately not `CONTRACT_STATUS`, and deliberately not `LeaseContract`. That document
 * belongs to Construction's property register (§4.1): it requires a `propertyId`, which
 * Real Estate cannot supply because §4.2 moved that register away from it, and it carries
 * escalation, deposits and a broker's commission that this section does not use. A tenancy
 * names its property the way a treat does — a number and a quarter, as free text (§4.2a).
 *
 * Three states, not five. There is no `draft`: a tenancy is agreed before it is typed, and
 * a draft one bills nothing, so the state would exist only to be skipped. There is no
 * `cancelled` distinct from `terminated` for the reason `LeaseContract` gives about its own
 * two endings — the difference that matters is whether the term **ran out** or whether
 * **somebody ended it**, and a tenancy typed in by mistake is deleted while it has raised
 * no invoice (I14) rather than parked in a fourth state.
 */
export const TENANCY_STATUS = {
  /** Running. Its schedule is live and its rows can be invoiced. */
  ACTIVE: 'active',
  /** The term ran out. Every period it promised was scheduled. */
  EXPIRED: 'expired',
  /** Somebody ended it early. The periods after the end date are cancelled. */
  TERMINATED: 'terminated',
};

/**
 * The rhythm a tenancy pays on, as a **number of months**.
 *
 * A number rather than `LEASE_FREQUENCY`'s four words, because the group asked for "every 1
 * month or more" and a landlord who collects every two months is not an exotic case — the
 * enum has no word for it and would have forced the rent to be misstated as monthly or
 * quarterly. These are the offered values, not a constraint: the schema's floor is 1 and
 * its ceiling is 12, and anything in between is a legitimate arrangement somebody typed.
 */
export const TENANCY_MONTH_OPTIONS = Object.freeze([1, 2, 3, 6, 12]);

export const DEAL_STAGE = {
  LEAD: 'lead',
  NEGOTIATION: 'negotiation',
  AGREED: 'agreed',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
};

/** Stages a deal can still be edited and moved between — `closed` is terminal. */
export const DEAL_OPEN_STAGES = [DEAL_STAGE.LEAD, DEAL_STAGE.NEGOTIATION, DEAL_STAGE.AGREED];

export const BROKER_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  INACTIVE: 'inactive',
};

/**
 * Every way a broker's capital balance moves (docs/10 §3.3).
 *
 * **Buying and selling move it, by the group's decision.** A treat purchase takes its cost
 * off his balance and the sale puts it back with his share of the outcome, so the figure on
 * his row is always the capital the group is holding for him *in cash* — the money he could
 * ask for today. This reverses the reading recorded in §9a, where a purchase left the
 * balance alone on the grounds that his total claim is unchanged.
 *
 * Both readings are defensible and the difference is **presentation, not solvency**: the
 * group's total debt to him is the same either way. What changed is where the rest of that
 * claim is shown while a treat is open — it moves to `2185 Broker Treat Investment`, so
 * `2180` and this balance stay the same number and "what do we owe Karwan?" is the two
 * added together.
 */
export const BROKER_MOVEMENT = {
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  /** His capital going into a treat — out of his cash balance, into the thing bought. */
  TRADE_BUY: 'trade_buy',
  /** …and coming back out of it, whether the treat sold or was cancelled. */
  TRADE_SELL: 'trade_sell',
  PROFIT_SHARE: 'profit_share',
  LOSS_SHARE: 'loss_share',
};

/** The directions that *increase* what the group owes the broker in cash. */
export const BROKER_MOVEMENT_IN = [
  BROKER_MOVEMENT.DEPOSIT,
  BROKER_MOVEMENT.TRADE_SELL,
  BROKER_MOVEMENT.PROFIT_SHARE,
];

// --- property owners (docs/10 §4.1a) ----------------------------------------

/**
 * **What moves a property owner's balance.**
 *
 * Construction builds a man's house with money he pays in first, so his balance is what is
 * *left* of what he gave — a liability in `2190`, the same shape a broker's capital takes
 * in `2180`. Every site expense charged to him draws it down.
 *
 * `expense_reversal` exists because an expense can be voided after it posted (**I5**), and
 * the correction has to be a row on his statement rather than the quiet disappearance of
 * the one it undoes.
 */
export const OWNER_MOVEMENT = {
  DEPOSIT: 'deposit',
  REFUND: 'refund',
  EXPENSE: 'expense',
  EXPENSE_REVERSAL: 'expense_reversal',
  /**
   * A deposit or a refund undone by a super admin (docs/10 §47). Its own direction rather
   * than a refund or a deposit written the other way, so that "paid in" stays what *he*
   * paid in and a corrected typo never reads as the owner taking money back.
   */
  DEPOSIT_REVERSAL: 'deposit_reversal',
  REFUND_REVERSAL: 'refund_reversal',
};

/** The directions that *increase* what the group still holds for the owner. */
export const OWNER_MOVEMENT_IN = [
  OWNER_MOVEMENT.DEPOSIT,
  OWNER_MOVEMENT.EXPENSE_REVERSAL,
  OWNER_MOVEMENT.REFUND_REVERSAL,
];

/** The two an operator enters by hand, and the only two the override can correct. */
export const OWNER_MONEY_MOVEMENTS = [OWNER_MOVEMENT.DEPOSIT, OWNER_MOVEMENT.REFUND];

export const OWNER_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  INACTIVE: 'inactive',
};

export const TRADE_STATUS = {
  OPEN: 'open',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
};

/**
 * **What a treat is** — the kinds of thing the group buys and sells (docs/10 §3.2).
 *
 * Shared by the Brokers treat (`Trade.assetRef`) and any later register that has to name
 * an asset, so the two cannot drift into two vocabularies for one list.
 */
export const ASSET_TYPE = {
  PROPERTY: 'property',
  VEHICLE: 'vehicle',
  LAND: 'land',
  GOODS: 'goods',
  OTHER: 'other',
};

export const ASSET_TYPES = Object.values(ASSET_TYPE);

/**
 * **Which details each kind of asset is worth asking for.**
 *
 * `type` and `label` alone were enough to *list* a treat and not enough to *recognise* one:
 * two apartments in the same building differ by their area and their floor, a car by its
 * plate. The broker's own sheet carries those, so a register that cannot hold them sends
 * the operator back to the paper it was meant to replace.
 *
 * A map rather than one flat field list, because the fields are not interchangeable — a
 * plate number on a plot of land is a question with no answer, and a form that asks it
 * teaches the operator to leave fields blank. The **schema accepts all of them** on every
 * type; this only decides what a form puts on screen, which is a presentation decision and
 * belongs where both the dialog and its tests can read it.
 *
 * Everything here is free text and optional. `area` in particular is written the way it is
 * said — "200 m²", "2 dunam" — because the group works in more than one unit, and a bare
 * number with a hidden unit is wrong for a year before anybody notices. None of it is
 * summed, filtered on, or posted.
 */
/**
 * **How the title to a plot is held.**
 *
 * `tapo` (تاپۆ) is the registered deed from the land registry; `kart` (کارت) is the
 * card issued against property the registry has not titled that way. The two are not
 * interchangeable and the difference decides what a buyer will pay — which is why it is a
 * fixed choice rather than a line of free text: “tapu”, “TAPO” and “تاپو” typed into a
 * text box are one fact the register cannot group by.
 *
 * Only land and property carry one. A car has a plate, not a deed.
 */
export const TITLE_DOC_TYPE = {
  TAPO: 'tapo',
  KART: 'kart',
};

export const TITLE_DOC_TYPES = Object.values(TITLE_DOC_TYPE);

export const ASSET_DETAIL_FIELDS = Object.freeze({
  [ASSET_TYPE.PROPERTY]: ['area', 'location', 'identifier', 'titleType', 'details'],
  [ASSET_TYPE.LAND]: ['area', 'location', 'identifier', 'titleType', 'details'],
  [ASSET_TYPE.VEHICLE]: ['identifier', 'details'],
  [ASSET_TYPE.GOODS]: ['identifier', 'details'],
  [ASSET_TYPE.OTHER]: ['location', 'details'],
});

/**
 * The detail fields that are a **choice** rather than a line of text, and what the choices
 * are. Everything not listed here is free text — see `ASSET_DETAIL_FIELDS`.
 */
export const ASSET_DETAIL_OPTIONS = Object.freeze({
  titleType: TITLE_DOC_TYPES,
});

/** Every detail key any type may carry — what the schema and the model accept. */
export const ASSET_DETAIL_KEYS = Object.freeze([...new Set(Object.values(ASSET_DETAIL_FIELDS).flat())]);

/** Which detail fields a form should show for `type`. Unknown types get the neutral set. */
export const assetDetailFieldsFor = (type) =>
  ASSET_DETAIL_FIELDS[type] ?? ASSET_DETAIL_FIELDS[ASSET_TYPE.OTHER];

export const PAYOUT_STATUS = {
  DRAFT: 'draft',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

export const PROJECT_STATUS = {
  PLANNED: 'planned',
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/** A project may only be certified against while it is running. */
export const PROJECT_CERTIFIABLE_STATUSES = [PROJECT_STATUS.ACTIVE];

/**
 * A progress certificate's own lifecycle.
 *
 * `approved` is the event: it consumes BOQ quantity and raises the draft invoice, both in
 * one transaction. A draft consumes nothing, which is why two of them can exist against the
 * same BOQ item and the second one fails on approval rather than at entry.
 */
export const CERTIFICATE_STATUS = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
};

/**
 * A subcontract runs the same shape as a head contract, minus the ledger.
 *
 * `active` is the state work may be certified against; `completed` is reached by certifying
 * the full value or by closing it short.
 */
export const SUBCONTRACT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  TERMINATED: 'terminated',
};

/** A variation order is a change to the contract, so it is approved or it is nothing. */
export const VARIATION_STATUS = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

// --- cleaner (docs/10 §50) ----------------------------------------------------

/**
 * **What was cleaned** — the type on a Cleaner order.
 *
 * A fixed list rather than free text so the register can be filtered and grouped by it:
 * "apartment", "Apartment" and "flat" typed into a box are one kind of job the report
 * cannot count. The keys are English slugs so the URL, the export and the database stay
 * stable; what the office reads is `cleaner:types.*` in each language.
 *
 * Order is the order the picker shows: buildings people live in, then commercial premises,
 * then public ones, then things that are not a room at all. `other` is last and is a real
 * answer, not a fallback.
 */
export const CLEANING_ORDER_TYPES = Object.freeze([
  'house',
  'apartment',
  'villa',
  'building',
  'shop',
  'office',
  'restaurant',
  'cafe',
  'hotel',
  'clinic',
  'hospital',
  'school',
  'mosque',
  'warehouse',
  'factory',
  'gym',
  'salon',
  'event_hall',
  /** Handover cleaning after a build — the job most likely to be done for Construction. */
  'construction_site',
  'windows',
  'car',
  'carpet',
  'sofa',
  'curtains',
  'mattress',
  'water_tank',
  'swimming_pool',
  'garden',
  'other',
]);

/**
 * A Cleaner order is **paid on entry**, so it has no draft: it exists `posted`, and the only
 * other state is `cancelled` — reversed, with the cash taken back out (**I5**).
 */
export const CLEANING_ORDER_STATUS = {
  POSTED: 'posted',
  CANCELLED: 'cancelled',
};

export const SERVICE_CONTRACT_TYPE = { RETAINER: 'retainer', PROJECT: 'project', CAMPAIGN: 'campaign' };

/**
 * A campaign's own lifecycle, distinct from `CONTRACT_STATUS`.
 *
 * `paused` is the reason it needs its own enum: a campaign is stopped and restarted without
 * the engagement behind it ending, and a contract has no equivalent state.
 */
export const CAMPAIGN_STATUS = {
  PLANNED: 'planned',
  RUNNING: 'running',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

// --- attachments ------------------------------------------------------------

export const ATTACHMENT_STATUS = {
  PENDING: 'pending',
  UPLOADED: 'uploaded',
  VERIFIED: 'verified',
  QUARANTINED: 'quarantined',
  FAILED: 'failed',
};

export const ATTACHMENT_OWNER_TYPES = [
  'invoice',
  'payment',
  'expense',
  'payroll',
  'commission',
  'journal',
  'reconciliation',
  'party',
  'property',
  'contract',
  'lease',
  'deal',
  'project',
  'certificate',
  'profit',
  'user',
];

export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'text/csv',
  'text/plain',
];

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/**
 * Image types a **browser** will render in an `<img>`.
 *
 * `image/heic` is deliberately absent although it is an allowed upload. It is what an
 * iPhone produces by default, so refusing it would reject half the receipts photographed
 * in the field — but no major browser decodes it, and an `<img>` pointing at one renders
 * a broken-image icon. It is stored, listed and downloadable; it simply gets the file
 * icon rather than a thumbnail, which is the truth rather than a broken picture.
 */
export const BROWSER_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Every image we accept, renderable or not — what counts as "a photo" in a list. */
export const IMAGE_MIME_TYPES = [...BROWSER_IMAGE_MIME_TYPES, 'image/heic'];

export const PDF_MIME_TYPE = 'application/pdf';

export const isImageMime = (mimeType) => IMAGE_MIME_TYPES.includes(mimeType);

/** Renders as a picture: only the types a browser can actually decode. */
export const isThumbnailableMime = (mimeType) => BROWSER_IMAGE_MIME_TYPES.includes(mimeType);

/** Opens inside the app — a picture or a PDF. Everything else downloads. */
export const isPreviewableMime = (mimeType) => isThumbnailableMime(mimeType) || mimeType === PDF_MIME_TYPE;

// --- misc -------------------------------------------------------------------

/**
 * UI languages. `ckb` is Kurdish Sorani — written in Arabic script and read
 * right-to-left, so it shares Arabic's `dir` handling but is a distinct language: a
 * missing Sorani string falls back to English, never to Arabic.
 */
export const LOCALES = ['en', 'ar', 'ckb'];

/** The two written right-to-left. Drives `<html dir>` and the logical-CSS layout. */
export const RTL_LOCALES = ['ar', 'ckb'];

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export const SUPPORTED_CURRENCIES = [
  'USD',
  'IQD',
  'USD',
  'SAR',
  'QAR',
  'KWD',
  'BHD',
  'OMR',
  'JOD',
  'EGP',
  'EUR',
  'GBP',
  'TRY',
];

// --- currencies -------------------------------------------------------------

/**
 * Rates are integers in micro-units: `rateMicro / 1_000_000` base-currency units buy one
 * unit of this currency (`FX_SCALE` in `money.js`). The base currency is pinned at
 * exactly `FX_SCALE`, which is what makes a same-currency conversion exact rather than
 * rounded.
 */
export const FX_RATE_SCALE = 1_000_000;

/**
 * What a fresh install ships with.
 *
 * **USD is the base**: the group reports in it, so every ledger line carries a USD twin
 * (I13). **IQD** is seeded alongside it and enabled from day one, because the business
 * collects and pays in both and a currency that has to be created before the first
 * receipt can be entered is a currency the system does not really support.
 *
 * Everything else is data — added, renamed, re-rated and deactivated through
 * `/api/v1/currencies` without a deploy. These two are seeded `isSystem` so they cannot
 * be deleted out from under the ledger that references them.
 *
 * The IQD rate below is a **starting figure, not a feed**. It says one dinar buys
 * 0.000763 USD (≈ 1,310 IQD to the dollar) and it is meant to be corrected on day one.
 */
export const DEFAULT_CURRENCIES = [
  {
    code: 'USD',
    name: 'US Dollar',
    nameAr: 'دولار أمريكي',
    nameCkb: 'دۆلاری ئەمریکی',
    symbol: '$',
    decimals: 2,
    isBase: true,
    rateMicro: FX_RATE_SCALE,
    sortOrder: 0,
  },
  {
    code: 'IQD',
    name: 'Iraqi Dinar',
    nameAr: 'دينار عراقي',
    nameCkb: 'دیناری عێراقی',
    symbol: 'ع.د',
    decimals: 3,
    isBase: false,
    rateMicro: 763,
    sortOrder: 1,
  },
];

/** The code a fresh install reports in, before anybody has configured anything. */
export const DEFAULT_BASE_CURRENCY = 'USD';
